import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AffiliateShell from "@/components/feature/AffiliateShell";
import EventMedia, { thumbWidth } from "@/components/feature/EventMedia";
import {
  cancelReservation,
  downloadIcs,
  EventFullError,
  fetchEvents,
  formatDateLong,
  formatDuration,
  formatTimeRange,
  googleCalendarUrl,
  isLive,
  isUpcoming,
  KIND_META,
  eventImage,
  reserveEvent,
  trackEvent,
  type CalEvent,
  type EventKind,
} from "@/services/events";

type Filter = "all" | EventKind;

const FILTERS: { key: Filter; label: string; icon: string }[] = [
  { key: "all", label: "Todas", icon: "ri-apps-2-line" },
  { key: "masterclass", label: KIND_META.masterclass.filterLabel, icon: "ri-live-line" },
  { key: "orientacion", label: KIND_META.orientacion.filterLabel, icon: "ri-user-heart-line" },
  { key: "entrenamiento", label: KIND_META.entrenamiento.filterLabel, icon: "ri-presentation-line" },
];

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const dayKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function Cover({ event, className = "", mode = "native" }: { event: CalEvent; className?: string; mode?: "native" | "fit" }) {
  return <EventMedia event={event} mode={mode} className={className} />;
}

const CARD_WIDTH = { horizontal: "w-full sm:w-56", square: "w-44 sm:w-40", vertical: "w-36 sm:w-36", none: "w-full sm:w-56" } as const;
const THUMB_WIDTH = { horizontal: "w-24", square: "w-14", vertical: "w-12", none: "w-14" } as const;

function AddToCalendar({ event, onDone, outline = false }: { event: CalEvent; onDone: (msg: string) => void; outline?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`w-full px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap ${outline ? "border border-violet-200 text-[#7C3AED] hover:bg-violet-50" : "bg-[#7C3AED] hover:bg-[#6D28D9] text-white"}`}
      >
        <i className="ri-calendar-event-line mr-1.5"></i>Agregar al calendario
      </button>
      {open ? (
        <div className="absolute right-0 left-0 sm:left-auto sm:w-56 mt-1 z-20 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          <a
            href={googleCalendarUrl(event)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="block px-3 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"
          >
            <i className="ri-google-fill mr-2"></i>Google Calendar
          </a>
          <button
            onClick={() => {
              downloadIcs(event);
              setOpen(false);
              onDone("Archivo de calendario descargado");
            }}
            className="w-full text-left px-3 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer border-t border-slate-100"
          >
            <i className="ri-download-2-line mr-2"></i>Apple / Outlook (.ics)
          </button>
        </div>
      ) : null}
    </div>
  );
}

function EventActions({
  event,
  busy,
  onReserve,
  onCancel,
  onToast,
}: {
  event: CalEvent;
  busy: boolean;
  onReserve: (e: CalEvent) => void;
  onCancel: (e: CalEvent) => void;
  onToast: (m: string) => void;
}) {
  const live = isLive(event);
  const full = event.capacity !== null && event.registered >= event.capacity && !event.reserved;
  return (
    <div className="flex flex-col gap-2 w-full sm:w-52">
      <span
        className={`self-start sm:self-end text-[11px] font-semibold px-2.5 py-1 rounded-full ${
          event.reserved ? "bg-emerald-50 text-emerald-700" : full ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-700"
        }`}
      >
        {event.reserved ? (
          <>
            Reservado <i className="ri-check-line"></i>
          </>
        ) : full ? (
          "Cupo lleno"
        ) : (
          <>
            <i className="ri-time-line"></i> Pendiente
          </>
        )}
      </span>
      {live && event.joinUrl ? (
        <a
          href={event.joinUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent(event.id, "join")}
          className="px-3 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold text-center cursor-pointer whitespace-nowrap"
        >
          <i className="ri-live-line mr-1.5"></i>Unirme ahora
        </a>
      ) : null}
      {event.reserved ? (
        <>
          <AddToCalendar event={event} onDone={onToast} />
          <button
            onClick={() => onCancel(event)}
            disabled={busy}
            className="px-3 py-2 rounded-lg border border-violet-200 text-[#7C3AED] text-xs font-semibold hover:bg-violet-50 disabled:opacity-50 cursor-pointer whitespace-nowrap"
          >
            Cancelar reserva
          </button>
        </>
      ) : (
        <>
          <button
            onClick={() => onReserve(event)}
            disabled={busy || full}
            className="px-3 py-2 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {busy ? "Reservando…" : "Reservar mi cupo"}
          </button>
          <AddToCalendar event={event} onDone={onToast} outline />
        </>
      )}
    </div>
  );
}

function EventDetail({ event, onClose, renderActions }: { event: CalEvent; onClose: () => void; renderActions: (e: CalEvent) => React.ReactNode }) {
  const meta = KIND_META[event.kind];
  const img = eventImage(event);
  const poster = img?.url ?? null;
  const posterIsVertical = !!img && img.shape !== "horizontal";
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={event.title}>
      <div className="fixed inset-0 bg-[#0B1F3A]/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl">
        <button onClick={onClose} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow cursor-pointer">
          <i className="ri-close-line text-lg"></i>
        </button>
        <div className={`grid ${poster && posterIsVertical ? "sm:grid-cols-[16rem_1fr]" : ""}`}>
          {poster ? (
            <div className={posterIsVertical ? "bg-slate-100" : ""}>
              <img src={poster} alt={`Imagen de ${event.title}`} className={posterIsVertical ? "w-full h-auto object-cover" : "w-full h-auto"} />
            </div>
          ) : null}
          <div className="p-5">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${meta.badge}`}>{meta.label.toUpperCase()}</span>
            <h2 className="mt-2 font-heading font-bold text-slate-900 text-lg leading-snug">{event.title}</h2>
            <ul className="mt-3 flex flex-col gap-1.5 text-sm text-slate-600">
              <li><i className="ri-calendar-line mr-1.5"></i>{formatDateLong(event.startsAt)}</li>
              <li><i className="ri-time-line mr-1.5"></i>{formatTimeRange(event)}</li>
              {event.hostName ? <li><i className="ri-user-3-line mr-1.5"></i>Con: {event.hostName}</li> : null}
              <li>
                <i className="ri-group-line mr-1.5"></i>
                {event.registered} reserva{event.registered === 1 ? "" : "s"}
                {event.capacity ? ` · cupo ${event.capacity}` : ""}
              </li>
            </ul>
            {event.description ? <p className="mt-3 text-sm text-slate-600 whitespace-pre-line">{event.description}</p> : null}
            <div className="mt-4">{renderActions(event)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendario mensual
// ---------------------------------------------------------------------------

function CalendarModal({
  events,
  initialMine,
  onClose,
  renderActions,
}: {
  events: CalEvent[];
  initialMine: boolean;
  onClose: () => void;
  renderActions: (e: CalEvent) => React.ReactNode;
}) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState<string>(dayKey(today));
  const [mine, setMine] = useState(initialMine);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const list = useMemo(() => events.filter((e) => (mine ? e.reserved : true)), [events, mine]);
  const byDay = useMemo(() => {
    const m = new Map<string, CalEvent[]>();
    list.forEach((e) => {
      const k = dayKey(new Date(e.startsAt));
      m.set(k, [...(m.get(k) ?? []), e]);
    });
    return m;
  }, [list]);

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i + 1))];
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = cursor.toLocaleDateString("es", { month: "long", year: "numeric" });
  const dayEvents = byDay.get(selected) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Calendario">
      <div className="fixed inset-0 bg-[#0B1F3A]/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-3xl max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-heading font-bold text-slate-900 text-lg">Calendario</h2>
          <button onClick={onClose} aria-label="Cerrar" className="w-8 h-8 rounded-full hover:bg-slate-100 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>
        <div className="mt-3 inline-flex rounded-lg bg-slate-100 p-1 text-xs font-semibold">
          {[
            { v: false, l: "Todos los eventos" },
            { v: true, l: "Mis reservas" },
          ].map((t) => (
            <button key={t.l} onClick={() => setMine(t.v)} className={`px-3 py-1.5 rounded-md cursor-pointer ${mine === t.v ? "bg-white shadow text-[#7C3AED]" : "text-slate-600"}`}>
              {t.l}
            </button>
          ))}
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="flex items-center justify-between">
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} aria-label="Mes anterior" className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <i className="ri-arrow-left-s-line"></i>
              </button>
              <div className="font-heading font-bold text-sm text-slate-900 capitalize">{monthLabel}</div>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} aria-label="Mes siguiente" className="w-8 h-8 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <i className="ri-arrow-right-s-line"></i>
              </button>
            </div>
            <div className="mt-3 grid grid-cols-7 text-center text-[10px] font-semibold text-slate-400">
              {WEEKDAYS.map((w) => (
                <div key={w}>{w}</div>
              ))}
            </div>
            <div className="mt-1 grid grid-cols-7 gap-y-1 text-center">
              {cells.map((d, i) => {
                if (!d) return <div key={i}></div>;
                const k = dayKey(d);
                const evs = byDay.get(k) ?? [];
                const isToday = k === dayKey(today);
                const isSel = k === selected;
                return (
                  <button
                    key={i}
                    onClick={() => setSelected(k)}
                    className={`mx-auto w-9 h-10 rounded-xl flex flex-col items-center justify-center text-xs font-semibold cursor-pointer ${
                      isSel ? "bg-[#7C3AED] text-white" : isToday ? "bg-violet-50 text-[#7C3AED]" : "text-slate-700 hover:bg-slate-50"
                    }`}
                    aria-label={`${d.getDate()}${evs.length ? `, ${evs.length} evento(s)` : ""}`}
                  >
                    {d.getDate()}
                    <span className="flex gap-0.5 h-1.5 mt-0.5">
                      {evs.slice(0, 3).map((e) => (
                        <span key={e.id} className="w-1.5 h-1.5 rounded-full" style={{ background: isSel ? "#fff" : KIND_META[e.kind].dot }}></span>
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-slate-500">
              {(Object.keys(KIND_META) as EventKind[]).map((k) => (
                <span key={k} className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: KIND_META[k].dot }}></span>
                  {KIND_META[k].label}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-bold text-slate-900">
              {(() => {
                const [y, m, d] = selected.split("-").map(Number);
                const s = new Date(y, m - 1, d).toLocaleDateString("es", { weekday: "long", day: "numeric", month: "long" });
                return s.charAt(0).toUpperCase() + s.slice(1);
              })()}
            </div>
            {dayEvents.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">{mine ? "No tienes reservas este día." : "No hay eventos este día."}</p>
            ) : (
              <ul className="mt-3 flex flex-col gap-3">
                {dayEvents.map((e) => (
                  <li key={e.id} className="rounded-xl border border-slate-200 p-3">
                    <div className="flex gap-3">
                      {eventImage(e) ? <EventMedia event={e} className={`rounded-lg flex-shrink-0 self-start ${thumbWidth(e, THUMB_WIDTH)}`} /> : null}
                      <div className="min-w-0">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${KIND_META[e.kind].badge}`}>{KIND_META[e.kind].label.toUpperCase()}</span>
                        <div className="mt-1 text-sm font-bold text-slate-900">{e.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{formatTimeRange(e)}</div>
                      </div>
                    </div>
                    <div className="mt-2">{renderActions(e)}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página
// ---------------------------------------------------------------------------

export default function Reuniones() {
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [calendar, setCalendar] = useState<null | { mine: boolean }>(null);
  const [detail, setDetail] = useState<CalEvent | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  const load = useCallback(async () => {
    try {
      setEvents(await fetchEvents());
      setError(false);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => {
    let active = true;
    load().finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [load]);

  const onReserve = async (e: CalEvent) => {
    setBusyId(e.id);
    try {
      await reserveEvent(e.id);
      showToast("¡Cupo reservado!");
    } catch (err) {
      showToast(err instanceof EventFullError ? "Este evento ya no tiene cupos." : "No se pudo reservar. Intenta de nuevo.");
    } finally {
      setBusyId(null);
      load();
    }
  };

  const onCancel = async (e: CalEvent) => {
    if (!window.confirm("¿Cancelar tu reserva para este evento?")) return;
    setBusyId(e.id);
    try {
      await cancelReservation(e.id);
      showToast("Reserva cancelada");
    } catch {
      showToast("No se pudo cancelar. Intenta de nuevo.");
    } finally {
      setBusyId(null);
      load();
    }
  };

  const upcoming = useMemo(() => events.filter((e) => isUpcoming(e)).filter((e) => filter === "all" || e.kind === filter), [events, filter]);
  const recordings = useMemo(
    () =>
      events
        .filter((e) => e.recordingUrl)
        .sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime()),
    [events],
  );

  const actions = (e: CalEvent) => <EventActions event={e} busy={busyId === e.id} onReserve={onReserve} onCancel={onCancel} onToast={showToast} />;

  return (
    <AffiliateShell>
      <div className="max-w-[900px] mx-auto flex flex-col gap-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0B1F3A]">Reuniones / Eventos</h1>
            <p className="text-sm text-slate-500 mt-1">Conéctate, aprende y crece junto a tu comunidad.</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Link to="/app/reuniones/panel" className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold hover:border-violet-300 cursor-pointer whitespace-nowrap">
              <i className="ri-bar-chart-2-line"></i> Vista avanzada
            </Link>
            <button
              onClick={() => setCalendar({ mine: true })}
              className="inline-flex items-center gap-2 bg-white border border-violet-200 text-[#7C3AED] rounded-xl px-3.5 py-2 text-xs font-semibold hover:bg-violet-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-2-line"></i> Mi calendario
            </button>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Filtrar eventos">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => setFilter(f.key)}
              className={`flex items-center gap-2 rounded-xl border px-3.5 py-2 text-xs font-semibold whitespace-nowrap cursor-pointer ${
                filter === f.key ? "bg-violet-50 border-[#7C3AED] text-[#7C3AED]" : "bg-white border-slate-200 text-slate-600 hover:border-violet-300"
              }`}
            >
              <i className={f.icon}></i>
              {f.label}
            </button>
          ))}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-slate-900">Próximas reuniones</h2>
            <button onClick={() => setCalendar({ mine: false })} className="text-xs font-semibold text-[#7C3AED] cursor-pointer">
              Ver calendario completo <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-500">
              <i className="ri-loader-4-line animate-spin text-3xl"></i>
            </div>
          ) : error ? (
            <div className="mt-3 rounded-2xl border border-dashed border-rose-300 p-8 text-center text-sm text-rose-700">
              No pudimos cargar los eventos.{" "}
              <button onClick={load} className="font-semibold underline cursor-pointer">
                Reintentar
              </button>
            </div>
          ) : upcoming.length === 0 ? (
            <div className="mt-3 rounded-2xl bg-white border border-slate-200 p-8 text-center">
              <span className="w-12 h-12 mx-auto rounded-full bg-violet-50 flex items-center justify-center">
                <i className="ri-calendar-close-line text-2xl text-[#7C3AED]"></i>
              </span>
              <h3 className="font-heading font-bold text-slate-900 mt-3">No hay reuniones programadas</h3>
              <p className="text-sm text-slate-500 mt-1">Cuando se publique una masterclass, orientación o entrenamiento, aparecerá aquí.</p>
            </div>
          ) : (
            <ul className="mt-3 flex flex-col gap-3">
              {upcoming.map((e) => {
                const meta = KIND_META[e.kind];
                const live = isLive(e);
                return (
                  <li key={e.id} className="bg-white border border-slate-200 rounded-2xl p-3 flex flex-col sm:flex-row gap-3 sm:items-stretch shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                    <div className={`relative flex-shrink-0 sm:self-start ${thumbWidth(e, CARD_WIDTH)}`}>
                      <Cover event={e} className="rounded-xl w-full" />
                      {live ? <span className="absolute top-2 left-2 bg-rose-600 text-white text-[10px] font-extrabold px-2 py-0.5 rounded">LIVE</span> : null}
                    </div>
                    <div className="flex-1 min-w-0 py-1">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${meta.badge}`}>{meta.label.toUpperCase()}</span>
                      <h3 className="mt-1.5 font-heading font-bold text-slate-900 leading-snug">{e.title}</h3>
                      <ul className="mt-2 flex flex-col gap-1 text-xs text-slate-500">
                        <li>
                          <i className="ri-calendar-line mr-1.5"></i>
                          {formatDateLong(e.startsAt)}
                        </li>
                        <li>
                          <i className="ri-time-line mr-1.5"></i>
                          {formatTimeRange(e)}
                        </li>
                        {e.hostName ? (
                          <li>
                            <i className="ri-user-3-line mr-1.5"></i>Con: {e.hostName}
                          </li>
                        ) : null}
                        <li>
                          <i className="ri-group-line mr-1.5"></i>
                          {e.registered} reserva{e.registered === 1 ? "" : "s"}
                          {e.capacity ? ` · cupo ${e.capacity}` : ""}
                        </li>
                      </ul>
                      {e.description ? <p className="mt-2 text-xs text-slate-600 line-clamp-2">{e.description}</p> : null}
                      <button onClick={() => setDetail(e)} className="mt-2 text-xs font-semibold text-[#7C3AED] hover:underline cursor-pointer">
                        Ver detalles <i className="ri-arrow-right-s-line"></i>
                      </button>
                    </div>
                    <div className="sm:self-center">{actions(e)}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-bold text-slate-900">Grabaciones disponibles</h2>
          </div>
          {recordings.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">Cuando una reunión tenga grabación, la encontrarás aquí.</p>
          ) : (
            <ul className="mt-3 flex gap-3 overflow-x-auto pb-2">
              {recordings.map((e) => (
                <li key={e.id} className="w-52 flex-shrink-0 bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="relative">
                    <Cover event={e} mode="fit" className="h-28" />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-10 h-10 rounded-full bg-white/90 text-[#7C3AED] flex items-center justify-center">
                        <i className="ri-play-fill text-xl"></i>
                      </span>
                    </span>
                    {e.recordingSeconds ? <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">{formatDuration(e.recordingSeconds)}</span> : null}
                  </div>
                  <div className="p-2.5">
                    <div className="text-xs font-bold text-slate-900 leading-snug line-clamp-2 min-h-[2rem]">{e.title}</div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      <i className="ri-calendar-line mr-1"></i>
                      {new Date(e.startsAt).toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                    <a
                      href={e.recordingUrl as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent(e.id, "recording")}
                      className="mt-2 block text-center text-xs font-semibold text-[#7C3AED] border border-violet-200 rounded-lg py-1.5 hover:bg-violet-50 cursor-pointer"
                    >
                      <i className="ri-play-line mr-1"></i>Ver grabación
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-violet-200 bg-violet-50/70 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <span className="w-12 h-12 rounded-full bg-[#7C3AED] text-white flex items-center justify-center text-2xl flex-shrink-0">
            <i className="ri-calendar-event-line"></i>
          </span>
          <div className="flex-1">
            <div className="font-heading font-bold text-slate-900 text-sm">No te pierdas nuestras reuniones en vivo</div>
            <p className="text-xs text-slate-600 mt-0.5">Participa en masterclass, orientaciones y entrenamientos. Cada reunión te acerca más a tus objetivos.</p>
          </div>
          <button onClick={() => setCalendar({ mine: false })} className="px-4 py-2.5 rounded-lg bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-sm font-semibold cursor-pointer whitespace-nowrap">
            <i className="ri-calendar-2-line mr-1.5"></i>Ver calendario completo
          </button>
        </div>
      </div>

      {detail ? <EventDetail event={events.find((x) => x.id === detail.id) ?? detail} onClose={() => setDetail(null)} renderActions={actions} /> : null}
      {calendar ? <CalendarModal events={events} initialMine={calendar.mine} onClose={() => setCalendar(null)} renderActions={actions} /> : null}
      {toast ? <div className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#0B1F3A] text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div> : null}
    </AffiliateShell>
  );
}
